/**
 * Maintenance Page Component - Service Interruption & Incident Response
 *
 * Displays during planned maintenance or unplanned service disruptions.
 * Provides clear communication to users when the platform is unavailable.
 *
 * Key architectural decisions:
 * - Full-screen overlay: Ensures users cannot access broken functionality
 * - Customisable messaging: Admin-configurable title/text via channel config
 * - Brand consistency: Uses channel branding even during downtime
 * - Graceful degradation: Works even if some assets fail to load
 *
 * Incident response strategy:
 * - Immediate activation: Can be enabled via channel.maintenance flag
 * - Clear messaging: Reduces support tickets during outages
 * - Return path: Provides navigation back to main site or external URL
 * - Visual continuity: Background image maintains brand presence
 *
 * Business rationale:
 * - Reduces customer anxiety during service interruptions
 * - Prevents frustrated users from repeatedly trying broken features
 * - Maintains professional appearance even during incidents
 * - Supports both planned maintenance and emergency scenarios
 *
 * User communication patterns:
 * - Title: Brief explanation (e.g., "Scheduled Maintenance")
 * - Text: Detailed message with expected resolution time
 * - Action: Clear path back to functional areas or external site
 * - Visual: Blurred background maintains context without distraction
 *
 * @see src/hooks/useChannel - Channel config including maintenance settings
 * @see src/ui/Button - Styled button for return navigation
 */

import styled from 'styled-components';
import { css } from '@emotion/react';
import Button from '../ui/Button';

/**
 * Channel configuration shape for maintenance mode.
 * WHY: Type safety for channel prop ensures maintenance config is valid
 */
export interface MaintenanceConfig {
  title?: string;
  text?: string;
}

export interface Channel {
  cover_image_url?: string;
  maintenance?: MaintenanceConfig;
  home_url?: string;
  logo_url: string;
}

export interface MaintenanceProps {
  channel: Channel;
}

// WHY: Absolute positioning with extreme z-index ensures nothing renders above
// This prevents users accessing broken features during maintenance
// Full viewport coverage blocks all interaction with underlying app
const StyledMaintenance = styled.div`
  width: 100vw;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;

  justify-content: center;
  /* background: var(--surface-secondary, #16181e); */
  z-index: 99999999999;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.4rem;
  padding: 2.4rem;
  z-index: 99999999999;

  width: 65rem;
`;

const Logo = styled.img`
  width: auto;

  height: 120px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #fff;
  padding: 0 32px;

  @media screen and (max-width: 767px) {
    padding: 0 0;
  }
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  color: #fff;
  padding: 0 32px;
  max-width: 430px;
`;

const Title = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 45px;
  text-align: center;

  @media screen and (max-width: 767px) {
    font-size: 28px;
    font-style: normal;
    font-weight: 300;
    text-align: center;
    line-height: 130%;
  }
`;

const Text = styled.div`
  color: var(--text-secondary, rgba(255, 255, 255, 0.8));
  text-align: center;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 200;
  line-height: 140%;
  max-width: 42rem;

  @media screen and (max-width: 767px) {
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 200;
    text-align: center;
    line-height: 130%;
  }
`;

const BackButton = styled(Button)`
  display: flex;
  height: 48px;
  min-width: 196px;
  padding: 0px var(--spacing-lg, 24px);
  justify-content: center;
  align-items: center;
  gap: var(--spacing-xs, 8px);
  align-self: stretch;
  border-radius: var(--radius-sm, 4px);
  border: 1px solid var(--text-primary, #fff);
`;

// WHY: Background image with blur creates depth without distraction
// Scale(1.05) prevents white edges when blur is applied
// Gradient overlay ensures text remains readable regardless of image
// Fallback to solid colour if image fails to load (graceful degradation)
const BackgroundImage = styled.div<{ $backgroundImage?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9;
  transform: scale(1.05);

  /* Full height */

  /* background-image: url("https://mdlwebstaticitems.s3.eu-west-2.amazonaws.com/images/1718194889526-bcfc_1.jpg"); */
  height: 100%;

  /* Add the blur effect */

  background: linear-gradient(
      0deg,
      var(--surface-scrim, rgba(19, 20, 25, 0.8)) 0%,
      var(--surface-scrim, rgba(19, 20, 25, 0.8)) 100%
    ),
    url("${(props) => props.$backgroundImage}") lightgray 50% / cover no-repeat;

  filter: blur(2.5px);

  -webkit-filter: blur(2.5px);

  ${(props) =>
    !props.$backgroundImage &&
    css`
      background: var(--surface-secondary, #16181e);
    `}
`;

function Maintenance({ channel }: MaintenanceProps): JSX.Element {
  const coverImage = channel.cover_image_url;
  // WHY: Default messages provide graceful fallback if admin hasn't configured
  // "Page currently unavailable" is generic enough for any incident type
  const title = channel.maintenance?.title || 'Page currently unavailable';
  // WHY: Apologetic tone with promise of resolution reduces user frustration
  // Research shows acknowledging inconvenience improves user patience
  const text =
    channel.maintenance?.text ||
    'Sorry for the inconvenience, we are currently doing some work on this page, but it will be available again soon.';
  const homeUrl = channel.home_url;

  return (
    <StyledMaintenance>
      <Container>
        {/* WHY: Logo maintains brand presence during service interruption
            Reassures users they're still on the correct platform */}
        <Logo src={channel.logo_url} />
        <TextContainer>
          <Title>{title}</Title>
          <Box>
            <Text>{text}</Text>
            {/* WHY: Return button provides clear escape route
                Uses window.location.assign for external URLs (cross-domain)
                Falls back to history.back() for internal navigation
                Transparent background maintains visual hierarchy */}
            <BackButton
              style={{ backgroundColor: 'transparent' }}
              onClick={() =>
                homeUrl
                  ? window.location.assign(homeUrl)
                  : window.history.back()
              }
            >
              Return Home
            </BackButton>
          </Box>
        </TextContainer>
      </Container>
      <BackgroundImage $backgroundImage={coverImage} />
    </StyledMaintenance>
  );
}

export default Maintenance;
